import { RefObject, useCallback, useEffect } from 'react'

/**
 * Data attribute used to mark elements that belong to a click-outside boundary.
 * Elements with this attribute (or their children) won't trigger outside click handlers
 * for boundaries with matching IDs.
 */
export const BOUNDARY_ATTRIBUTE = 'data-click-boundary'

/**
 * Checks if an element is inside a boundary with the given ID.
 * This checks the element itself and all of its ancestors.
 */
const isInsideBoundary = (
  element: Element | null,
  boundaryId: string,
): boolean => {
  let current = element
  while (current) {
    const boundaryValue = current.getAttribute(BOUNDARY_ATTRIBUTE)
    if (boundaryValue) {
      // Check if this boundary matches our ID (supports comma-separated IDs)
      const boundaryIds = boundaryValue.split(',').map(id => id.trim())
      if (boundaryIds.includes(boundaryId)) {
        return true
      }
    }
    current = current.parentElement
  }
  return false
}

/**
 * ARIA roles that indicate floating/overlay UI elements
 */
const FLOATING_ROLES = new Set([
  'listbox',
  'option',
  'menu',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'dialog',
  'alertdialog',
  'tooltip',
  'combobox',
])

/**
 * Data attributes commonly used by UI libraries for portals and floating elements.
 * This list covers: Chakra UI, Radix UI, Headless UI, Reach UI, Floating UI, Popper.js
 */
const FLOATING_DATA_ATTRIBUTES = [
  'data-portal',
  'data-chakra-portal',
  'data-radix-portal',
  'data-radix-popper-content-wrapper',
  'data-floating-ui-portal',
  'data-headlessui-portal',
  'data-reach-portal',
  'data-popper-placement',
  'data-popover-card', // our PopoverCard (date/range calendars, etc.)
]

/**
 * Regex pattern to match portal/floating-related class names and IDs
 */
const FLOATING_PATTERN = /portal|popover|overlay|^react-select-/i

/**
 * Checks if an element is inside any floating UI element like portaled menus.
 * Uses dynamic detection by walking up the DOM tree and checking for:
 * - ARIA roles that indicate floating content (listbox, menu, dialog, etc.)
 * - Common data attributes used by UI libraries for portals
 * - Class names or IDs matching portal/floating patterns
 * - Elements rendered outside the main app root (portal containers)
 */
const isInsideFloatingUI = (element: Element | null): boolean => {
  let current = element

  while (current) {
    // Check for floating ARIA roles
    const role = current.getAttribute('role')
    if (role && FLOATING_ROLES.has(role)) {
      return true
    }

    // Check for common portal/floating UI data attributes
    for (const attr of FLOATING_DATA_ATTRIBUTES) {
      if (current.hasAttribute(attr)) {
        return true
      }
    }

    // Check class names and ID against floating patterns
    const className =
      typeof current.className === 'string' ? current.className : ''
    const id = current.id || ''

    if (FLOATING_PATTERN.test(className) || FLOATING_PATTERN.test(id)) {
      return true
    }

    current = current.parentElement
  }

  // Check if element is rendered outside the main app root (catches unmarked portals)
  const appRoot =
    document.getElementById('root') || document.getElementById('app')
  if (appRoot && element && !appRoot.contains(element)) {
    return true
  }

  return false
}

export type UseClickOutsideBoundaryOptions = {
  /**
   * Ref to the main container element
   */
  ref: RefObject<HTMLElement | null>
  /**
   * Unique identifier for this boundary. Used to match with data-click-boundary attributes.
   */
  boundaryId: string
  /**
   * Handler called when a click is detected outside the boundary
   */
  onClickOutside: () => void
  /**
   * Whether the click outside detection is enabled
   * @default true
   */
  enabled?: boolean
  /**
   * Whether to automatically detect and exclude floating UI elements
   * (react-select menus, Chakra portals, etc.)
   * @default true
   */
  excludeFloatingUI?: boolean
}

/**
 * Hook that detects clicks outside a boundary and calls a handler.
 *
 * This is designed to work well with portaled elements like dropdown menus.
 * It uses a data-attribute based system to mark elements that should be
 * considered "inside" the boundary, even if they're portaled elsewhere in the DOM.
 *
 * ## Usage
 *
 * 1. Wrap your container with the boundary attribute:
 * ```tsx
 * <div ref={containerRef} data-click-boundary="my-popover">
 *   <Content />
 * </div>
 * ```
 *
 * 2. Mark portaled elements that should be considered "inside":
 * ```tsx
 * <Portal>
 *   <div data-click-boundary="my-popover">
 *     <DropdownMenu />
 *   </div>
 * </Portal>
 * ```
 *
 * 3. Use the hook:
 * ```tsx
 * useClickOutsideBoundary({
 *   ref: containerRef,
 *   boundaryId: 'my-popover',
 *   onClickOutside: handleClose,
 *   enabled: isOpen,
 * })
 * ```
 *
 * The hook will also automatically detect and exclude clicks on common floating
 * UI elements like react-select menus and Chakra portals.
 */
export const useClickOutsideBoundary = ({
  ref,
  boundaryId,
  onClickOutside,
  enabled = true,
  excludeFloatingUI = true,
}: UseClickOutsideBoundaryOptions) => {
  const handleClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Element | null

      if (!target) return

      // Check if click is inside the main ref
      if (ref.current && ref.current.contains(target)) {
        return
      }

      // Check if click is inside any element marked with the boundary attribute
      if (isInsideBoundary(target, boundaryId)) {
        return
      }

      // Optionally check for floating UI elements
      if (excludeFloatingUI && isInsideFloatingUI(target)) {
        return
      }

      // Click is outside - call the handler
      onClickOutside()
    },
    [ref, boundaryId, onClickOutside, excludeFloatingUI],
  )

  useEffect(() => {
    if (!enabled) return

    // Use mousedown for more responsive feel (fires before focus changes)
    document.addEventListener('mousedown', handleClick)

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [enabled, handleClick])
}

/**
 * Helper function to generate the boundary attribute value for a given ID.
 * Can combine multiple boundary IDs.
 */
export const getBoundaryProps = (boundaryId: string | string[]) => {
  const ids = Array.isArray(boundaryId) ? boundaryId : [boundaryId]
  return {
    [BOUNDARY_ATTRIBUTE]: ids.join(','),
  }
}
